import axios, { AxiosInstance } from "axios";
import { Capacitor } from "@capacitor/core";
import { clearTokens, getRefreshToken, getToken, storeRefreshToken, storeToken } from "./storage/tokenStorage";

type UseApiProps = {
  onLogout?: () => void
}

export function useApi(props?: UseApiProps) {
  const onLogout = props?.onLogout;
  const plateform: string = Capacitor.getPlatform(); // "android", "ios", "web"
  const isNative = Capacitor.isNativePlatform();

  let urlBase;

  if (import.meta.env.PROD && plateform === "web") {
    urlBase = import.meta.env.VITE_API_URL
  } else if (import.meta.env.PROD && plateform === "android") {
    urlBase = import.meta.env.VITE_API_URL_MOBILE_PROD
  } else if (import.meta.env.DEV && plateform === "android") {
    urlBase = import.meta.env.VITE_API_URL_MOBILE_DEV;
  } else if (import.meta.env.DEV && plateform === "web") {
    urlBase = import.meta.env.VITE_API_URL_DEV
  } else {
    throw new Error("environnement et plateforme inconnue");
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  if (isNative) {
    headers["X-Requested-With"] = "com.cemade.troc";
  }

  const api: AxiosInstance = axios.create({
    baseURL: urlBase,

    //headers
    headers,

  })

  api.interceptors.request.use(async (config) => {
    try {
      // Routes à exclure
      const publicPaths = ['auth/login', 'auth/signup', 'contact', 'existing/pseudo', 'ask-pwd-forgot', 'submit-reset-password', 'openai/assistant-cemade-ai'];
      const isPublicPath = publicPaths.some((path) =>
        config.url?.includes(path)
      );

      // Si route publique → on laisse passer la requête sans token
      if (isPublicPath) {
        return config;
      }

      //get token
      const token = await getToken();

      if (token) {
        //Ajouter le Token dans le header
        config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      } else {
        // Suppression des tokens si jamais il y en avait un en cache
        await clearTokens();
        localStorage.removeItem('persist:root');
        window.location.href = '/';
        return Promise.reject(new Error("Aucun token trouvé, requête annulée."));
      }
    } catch (error) {
      // En cas d’erreur dans getToken, on rejette aussi
      // Suppression des tokens si jamais il y en avait un en cache
      await clearTokens();
      localStorage.removeItem('persist:root');
      window.location.href = '/';
      return Promise.reject(error);
    }
  }, (error) => {
    // Gestion d'erreur globale de l'intercepteur
    return Promise.reject(error);
  });

  api.interceptors.response.use(
    (response) => response,
    async (error) => {

      if (error.response?.status === 401 && error.response.data?.message === "Token expired") {
        const originalRequest = error.config;

        let refreshToken = await getRefreshToken();

        if (refreshToken) {
          try {
            // Faire une requête pour obtenir un nouveau token en envoyant le refreshToken dans le header
            const { data } = await axios.post(`${import.meta.env.PROD ? import.meta.env.VITE_API_URL : import.meta.env.VITE_API_URL_DEV}/auth/refreshToken`, {}, {
              headers: {
                'Authorization': `Bearer ${refreshToken}`
              }
            });

            // Mettre à jour le token et refreshToken dans le local storage
            await storeToken(data.token);
            await storeRefreshToken(data.refreshToken);

            // Mettre à jour le header Authorization de la requête initiale
            originalRequest.headers['Authorization'] = `Bearer ${data.token}`;

            // Réessayer la requête initiale
            return api(originalRequest);
          }
          catch (refreshTokenError) {
            // Gérer les erreurs de rafraîchissement de token (ex. suppression des tokens et rediriger vers la page de connexion)
            await clearTokens();

            window.location.replace("/");
            return;
          }
        } else {
          // Suppression des tokens si jamais il y en avait un en cache
          await clearTokens();

          alert("Session expirée. Merci de vous reconnecter.");

          // Redirection vers la page de connexion ou d'accueil
          window.location.replace("/");
          return;
        }
      }

      // Cas : Aucun token fourni
      if (error.response?.status === 401 && error.response.data?.message === "Token missing") {
        // Suppression des tokens si jamais il y en avait un en cache
        await clearTokens();
        onLogout?.();
        // Redirection vers la page de connexion ou d'accueil
        window.location.replace("/");
        return;
      }

      // Cas : Erreur serveur
      if (error.response && error.response.status === 500) {
        //Vous pouvez rediriger l'utilisateur vers une page d'erreur personnalisée.
        //Si l'erreur 500 se produit, vous pouvez retourner une valeur par défaut ou un objet vide pour éviter de casser votre application.
        //Vous pouvez envoyer l'erreur à un service de journalisation pour suivre les problèmes sur le serveur.
        //Vous pouvez afficher un message d'erreur à l'utilisateur, lui indiquant qu'il y a un problème technique et qu'il peut essayer à nouveau plus tard. exemple une alert
      }

      return Promise.reject(error);
    }
  );

  return api;
}